import { PageHeader, Badge } from '@/shared/components';
import { SchedulePreviewTable } from '../../SchedulePreviewTable';
import { isValidPaymentSelection } from '../../../types/schedule.types';
import type { PaymentSelection } from '../../../types/schedule.types';
import { resolveAppliedDeposit } from '../../../services/appliedDeposit';
import { isIllustrativeScheduleMethod } from '../../../utils/illustrativeSchedule';
import { schedulingMethodLabel } from '../../../utils/schedulingMethodLabel';
import type { SchedulingMethod } from '@/features/products/types/products.types';
import { readOnlyDepositStatement } from './copy';

interface ReadOnlyPaymentViewProps {
    methods: SchedulingMethod[];
    paymentSelection: PaymentSelection | undefined;
    currency: string;
}

export function ReadOnlyPaymentView({
    methods,
    paymentSelection,
    currency,
}: ReadOnlyPaymentViewProps) {
    const confirmedAppliedDeposit = isValidPaymentSelection(paymentSelection)
        ? resolveAppliedDeposit(paymentSelection)
        : undefined;
    const readOnlyDeposit = readOnlyDepositStatement(confirmedAppliedDeposit, currency);

    return (
        <div className="space-y-6">
            <PageHeader
                title="Payment Options"
                subtitle="This quote is no longer live — the payment schedule cannot be changed."
            />
            {isValidPaymentSelection(paymentSelection) ? (
                <div
                    data-testid="schedule-preview-readonly"
                    className="p-6 bg-mp-surface border border-mp-border rounded-lg space-y-4"
                >
                    <div className="flex items-center gap-2">
                        <Badge variant="info">
                            {schedulingMethodLabel(methods, paymentSelection.methodKey)}
                        </Badge>
                        <span className="text-xs text-mp-muted">
                            Confirmed schedule (read-only)
                        </span>
                    </div>
                    {readOnlyDeposit !== null && (
                        <p
                            data-testid="applied-deposit-readonly"
                            className="text-xs text-mp-muted"
                        >
                            {readOnlyDeposit.lead}
                            {readOnlyDeposit.figure !== undefined && (
                                <>
                                    {': '}
                                    <span className="font-medium text-mp-foreground">
                                        {readOnlyDeposit.figure}
                                    </span>
                                </>
                            )}
                        </p>
                    )}
                    <SchedulePreviewTable
                        schedule={paymentSelection.snapshot.schedule}
                        meta={paymentSelection.snapshot.meta}
                        numPayments={paymentSelection.snapshot.numPayments}
                        currency={currency}
                        depositRowConfirmed={confirmedAppliedDeposit?.depositRowConfirmed === true}
                        anchorDate={paymentSelection.scheduleAnchorDate}
                        illustrativeDates={isIllustrativeScheduleMethod(paymentSelection.methodKey)}
                    />
                </div>
            ) : (
                <div
                    data-testid="schedule-preview-readonly-empty"
                    className="p-6 bg-mp-background border border-mp-border rounded-lg text-center"
                >
                    <p className="text-sm text-mp-muted">
                        No payment schedule was selected for this quote.
                    </p>
                </div>
            )}
        </div>
    );
}
